/**
 * SheetSink: per-sheet Layer 0 builder.
 *
 * Allocates typed arrays at construction; `ingest(chunk)` walks each
 * chunk from the chunked reader once and fills the Layer 0
 * classification (two-axis origin × value-kind), the interned formula
 * and number-format ids, and the row/column density projections.
 * `getOutput()` returns the bundle in the exact shape the SheetModel
 * needs.
 *
 * Interning is workbook-wide via the shared catalogs: equal R1C1
 * patterns across all sheets collapse to one id, which is the key
 * memory win at 1M+ formula cells.
 *
 * The sink is allocated once per sheet, fed in chunk order, and
 * discarded. It deliberately holds no references to Office.js proxies
 * — those die with each chunk handed in by the reader.
 *
 * Known limitations (intentional for Phase B; revisit later):
 *   - Spill catalog is empty (per Phase 5a), so dynamic-array interiors
 *     are classified CONSTANT instead of SPILL_RESULT.
 *   - Errors returned by Office.js as objects rather than strings fall
 *     through to NONE / TEXT instead of ERROR.
 *   - A constant cell whose displayed text begins with `=` (forced via
 *     text format) is misclassified as FORMULA. Architecture doc flags
 *     this as a platform ambiguity.
 */

import type {
  OriginValue,
  SheetBounds,
  ValueKindValue,
  WorkbookCatalogs,
} from "../types";
import { Origin, ValueKind } from "../types";
import type { IngestionTarget } from "./catalogs";
import type { SheetChunk } from "./chunkedReader";

/** Layer 0 output bundle — populates the matching fields on SheetModel. */
export interface SheetSinkOutput {
  bounds: SheetBounds;
  cellOrigin: Int8Array;
  cellValueKind: Int8Array;
  numericGrid: Float64Array;
  r1c1Ids: Int32Array;
  formulaA1Ids: Int32Array;
  numFmtIds: Int32Array;
  textValueIds: Int32Array;
  rowDensity: Int32Array;
  colDensity: Int32Array;
}

export class SheetSink {
  private readonly bounds: SheetBounds;
  private readonly cellOrigin: Int8Array;
  private readonly cellValueKind: Int8Array;
  private readonly numericGrid: Float64Array;
  private readonly r1c1Ids: Int32Array;
  private readonly formulaA1Ids: Int32Array;
  private readonly numFmtIds: Int32Array;
  private readonly textValueIds: Int32Array;
  private readonly rowDensity: Int32Array;
  private readonly colDensity: Int32Array;
  private readonly catalogs: WorkbookCatalogs;

  // Spill predicates cached as plain functions at construction so the
  // hot inner loop doesn't pay a Map.get + method-dispatch cost per
  // cell. Today both predicates always return false (empty spill
  // catalog) — they exist so the loop shape is right for Phase 5a.
  private readonly spillContains: (r: number, c: number) => boolean;
  private readonly spillIsAnchor: (r: number, c: number) => boolean;

  constructor(target: IngestionTarget, catalogs: WorkbookCatalogs) {
    const { rows, cols } = target.bounds;
    const n = rows * cols;

    this.bounds = target.bounds;
    this.catalogs = catalogs;

    this.cellOrigin = new Int8Array(n); // defaults to 0 = EMPTY
    this.cellValueKind = new Int8Array(n); // defaults to 0 = NONE
    this.numericGrid = new Float64Array(n);
    this.numericGrid.fill(NaN); // empty/non-numeric must read as NaN, not 0
    this.r1c1Ids = new Int32Array(n);
    this.r1c1Ids.fill(-1);
    this.formulaA1Ids = new Int32Array(n);
    this.formulaA1Ids.fill(-1);
    this.numFmtIds = new Int32Array(n);
    this.numFmtIds.fill(-1);
    this.textValueIds = new Int32Array(n);
    this.textValueIds.fill(-1);
    this.rowDensity = new Int32Array(rows);
    this.colDensity = new Int32Array(cols);

    const spill = catalogs.spillCatalog.get(target.sheetId);
    if (spill) {
      this.spillContains = spill.contains.bind(spill);
      this.spillIsAnchor = spill.isAnchor.bind(spill);
    } else {
      this.spillContains = () => false;
      this.spillIsAnchor = () => false;
    }
  }

  /**
   * Process one chunk from the chunked reader. Walks the chunk's
   * `rows × cols` grid once, writing into the sink's flat arrays.
   * Hoists per-cell catalog references and bound coordinates outside
   * the inner loop for cache efficiency.
   */
  ingest(chunk: SheetChunk): void {
    const { values, formulasR1C1, formulas, numberFormat, rowOffset, rows, cols } = chunk;
    const totalCols = this.bounds.cols;
    const absStartRow = this.bounds.startRow;
    const absStartCol = this.bounds.startCol;

    // Hoist catalog refs for the inner loop
    const r1c1Dict = this.catalogs.r1c1Dict;
    const r1c1Strings = this.catalogs.r1c1Strings;
    const formulaA1Dict = this.catalogs.formulaA1Dict;
    const formulaA1Strings = this.catalogs.formulaA1Strings;
    const numFmtDict = this.catalogs.numFmtDict;
    const numFmtStrings = this.catalogs.numFmtStrings;
    const textValueDict = this.catalogs.textValueDict;
    const textValueStrings = this.catalogs.textValueStrings;

    for (let i = 0; i < rows; i++) {
      const valRow = values[i];
      const fmlRow = formulasR1C1[i];
      const fmlA1Row = formulas[i];
      const fmtRow = numberFormat[i];
      const localRow = rowOffset + i;
      const absRow = absStartRow + localRow;
      const rowBase = localRow * totalCols;

      for (let j = 0; j < cols; j++) {
        const idx = rowBase + j;
        const v = valRow[j];
        const f = fmlRow[j];
        const fmt = fmtRow[j];
        const absCol = absStartCol + j;

        // ---------- Axis 1: ORIGIN ----------
        let origin: OriginValue;
        if (this.spillContains(absRow, absCol) && !this.spillIsAnchor(absRow, absCol)) {
          origin = Origin.SPILL_RESULT;
        } else if (typeof f === "string" && f.charCodeAt(0) === 61) {
          // 61 = '='
          origin = Origin.FORMULA;
        } else if (v === null || v === "" || v === undefined) {
          origin = Origin.EMPTY;
        } else {
          origin = Origin.CONSTANT;
        }
        this.cellOrigin[idx] = origin;

        // ---------- Axis 2: VALUE KIND ----------
        let valueKind: ValueKindValue;
        if (origin === Origin.EMPTY) {
          valueKind = ValueKind.NONE;
        } else if (typeof v === "number") {
          valueKind =
            typeof fmt === "string" && isDateFormat(fmt)
              ? ValueKind.DATE
              : ValueKind.NUMBER;
          this.numericGrid[idx] = v;
        } else if (typeof v === "boolean") {
          valueKind = ValueKind.BOOLEAN;
        } else if (typeof v === "string") {
          if (isErrorLiteral(v)) {
            valueKind = ValueKind.ERROR;
          } else {
            valueKind = ValueKind.TEXT;
            this.textValueIds[idx] = intern(textValueDict, textValueStrings, v);
          }
        } else {
          valueKind = ValueKind.NONE;
        }
        this.cellValueKind[idx] = valueKind;

        // ---------- Interning + densities (skip for EMPTY) ----------
        if (origin === Origin.FORMULA) {
          this.r1c1Ids[idx] = intern(r1c1Dict, r1c1Strings, f as string);
          // A1 form parallels R1C1 — Office.js gives both; we intern
          // each into its own workbook-wide dict.
          const fA1 = fmlA1Row[j];
          if (typeof fA1 === "string") {
            this.formulaA1Ids[idx] = intern(formulaA1Dict, formulaA1Strings, fA1);
          }
        }
        if (origin !== Origin.EMPTY) {
          if (typeof fmt === "string") {
            this.numFmtIds[idx] = intern(numFmtDict, numFmtStrings, fmt);
          }
          this.rowDensity[localRow]++;
          this.colDensity[j]++;
        }
      }
    }
  }

  getOutput(): SheetSinkOutput {
    return {
      bounds: this.bounds,
      cellOrigin: this.cellOrigin,
      cellValueKind: this.cellValueKind,
      numericGrid: this.numericGrid,
      r1c1Ids: this.r1c1Ids,
      formulaA1Ids: this.formulaA1Ids,
      numFmtIds: this.numFmtIds,
      textValueIds: this.textValueIds,
      rowDensity: this.rowDensity,
      colDensity: this.colDensity,
    };
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Intern a string into a workbook-wide dict + parallel reverse-lookup
 * array. O(1) amortized. New ids monotonically grow `strings.length`,
 * so `strings[id]` always recovers the original.
 */
function intern(dict: Map<string, number>, strings: string[], s: string): number {
  const existing = dict.get(s);
  if (existing !== undefined) return existing;
  const id = strings.length;
  dict.set(s, id);
  strings.push(s);
  return id;
}

/**
 * Heuristic: format code is a date/time format.
 *
 * Strips quoted literals first (Excel formats use `"..."` for literal
 * text that may contain date-like letters — e.g. `"0\"M\""` for
 * "millions"). Then matches any of the date/time tokens y/m/d/h/s or
 * the AM/PM marker.
 *
 * False positives possible on custom formats that use these letters
 * outside quotes for non-date purposes; good enough for first-pass
 * classification.
 */
function isDateFormat(fmt: string): boolean {
  if (!fmt) return false;
  const stripped = fmt.replace(/"[^"]*"/g, "");
  return /[yYmMdDhHsS]/.test(stripped) || /AM\/PM|am\/pm/.test(stripped);
}

/**
 * Excel error literals returned as strings. The architecture flags that
 * Office.js may also return errors as objects on some platforms; that
 * shape isn't classified as ERROR here and falls through to NONE
 * (object value) or TEXT (some odd toString). Worth re-verifying when
 * we exercise this against real models.
 */
const ERROR_LITERALS = new Set([
  "#REF!",
  "#VALUE!",
  "#N/A",
  "#DIV/0!",
  "#NULL!",
  "#NAME?",
  "#NUM!",
  "#GETTING_DATA",
  "#SPILL!",
  "#CALC!",
  "#BLOCKED!",
]);

function isErrorLiteral(v: string): boolean {
  // Cheap prefilter: error literals all start with '#' (char code 35).
  return v.charCodeAt(0) === 35 && ERROR_LITERALS.has(v);
}
